import React, { FunctionComponent, useRef, useState, useEffect } from 'react';
import AppButton from 'components/AppButton/AppButton';
import { CSVLink } from 'react-csv';
import { DownloadIcon } from 'evergreen-ui';
import { FeedbackList } from '../../../typings/target_group';
import LinearProgress from '@material-ui/core/LinearProgress';
import {
    useLazyGetFeedbackQuery,
    useLazyGetParticipantsQuery,
} from 'store/api/participants';

export interface TargetGroupFeedbackDownloadProps {
    companyId: string;
    companyName: string;
    groupId: string;
    assessmentName: string | undefined;
    targetGroupName: string;
    assessmentLanguage: string | undefined;
    totalParticipants: number;
}

const TargetGroupFeedbackDownload: FunctionComponent<
    TargetGroupFeedbackDownloadProps
> = (props) => {
    const {
        companyId,
        companyName,
        groupId,
        assessmentName,
        assessmentLanguage,
        targetGroupName,
        totalParticipants,
    } = props;

    const [getParticipants] = useLazyGetParticipantsQuery();
    const [getFeedback] = useLazyGetFeedbackQuery();

    const csvLink = useRef<
        CSVLink & HTMLAnchorElement & { link: HTMLAnchorElement }
    >(null);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [isButtonDisabled, setIsButtonDisabled] = useState<boolean>(false);
    const [isDone, setIsDone] = useState(false);
    const [processedCount, setProcessedCount] = useState<number>(0);

    const [feedbackCsvData, setFeedbackCsvData] = useState<
        {
            [p: string]: string | number | undefined;
            'Scan name': string | undefined;
            'Group name': string;
            'Candidate ID': string;
        }[]
    >([]);

    const experienceQuestion =
        assessmentLanguage === 'nl-NL'
            ? 'Beschrijf hoe je ervaring was tijdens het uitvoeren van deze scan.'
            : 'Please describe your experience taking this scan.';
    const suggestionsQuestion =
        assessmentLanguage === 'nl-NL'
            ? 'Is er nog iets dat je met ons of ' +
              companyName +
              ' zou willen delen?'
            : 'Is there anything you want to share with us or ' +
              companyName +
              '?';
    const feedbackRate =
        assessmentLanguage === 'nl-NL'
            ? 'Hoe zou je je ervaring tijdens het uitvoeren van de scan beoordelen?'
            : 'How would you rate your experience doing this scan?';

    function wait(ms: number) {
        return new Promise((resolve) => setTimeout(resolve, ms));
    }

    const downloadFeedback = async () => {
        const limit = 80;
        let feedback: FeedbackList = {};

        const getData = async (nextToken: string) => {
            try {
                const { participants, nextToken: nextNextToken } =
                    await getParticipants({
                        companyIdentifier: companyId,
                        groupId,
                        limit,
                        includeResults: false,
                        includeTotal: false,
                        nextToken,
                    }).unwrap();

                const feedbackList = await getFeedback({
                    companyIdentifier: companyId,
                    groupId,
                    userIds: participants.map((p) => p.user_id),
                }).unwrap();

                feedback = { ...feedback, ...feedbackList };

                setProcessedCount((prev) => prev + participants.length);

                if (nextNextToken) {
                    await wait(1000);
                    await getData(nextNextToken);
                }
            } catch (e) {
                console.log('Error getting feedback', e); // todo
            }
        };

        setIsSaving(true);
        setProcessedCount(0);
        await getData('');

        if (Object.keys(feedback).length) {
            setFeedbackCsvData(
                Object.keys(feedback).map((userId) => ({
                    'Scan name': assessmentName,
                    'Group name': targetGroupName,
                    'Candidate ID': userId,
                    [experienceQuestion]: feedback[userId].experienceDescription
                        ? feedback[userId].experienceDescription
                        : '-',
                    [suggestionsQuestion]: feedback[userId].improveSuggestion
                        ? feedback[userId].improveSuggestion
                        : '-',
                    [feedbackRate]: feedback[userId].feedbackRate
                        ? feedback[userId].feedbackRate
                        : '-',
                })),
            );
        }

        if (!Object.keys(feedback).length) {
            setIsButtonDisabled(true);
        }
        setIsDone(true);
    };

    useEffect(() => {
        if (feedbackCsvData.length && isSaving) {
            csvLink?.current?.link.click();
            setIsSaving(false);
            setProcessedCount(0);
        }
    }, [feedbackCsvData, isSaving]);

    const progress = totalParticipants
        ? Math.min((processedCount / totalParticipants) * 100, 100)
        : 0;

    return (
        <div aria-label="download feedback button">
            {isSaving && !isDone && (
                <div>
                    <LinearProgress variant="determinate" value={progress} />
                    <div style={{ fontSize: 12, marginTop: 4 }}>
                        Downloading feedback…
                    </div>
                </div>
            )}
            {(isDone || !isSaving) && (
                <AppButton
                    justifyContent={'flex-start'}
                    width={'auto'}
                    icon={DownloadIcon}
                    isDisabled={isSaving || isButtonDisabled}
                    onClick={() => downloadFeedback()}>
                    Download all feedback
                </AppButton>
            )}
            <CSVLink
                data={feedbackCsvData}
                filename={companyName + '-' + targetGroupName + '-feedback.csv'}
                className="hidden"
                ref={csvLink}
                target={'_blank'}
            />
        </div>
    );
};

export default TargetGroupFeedbackDownload;
